import { DialogProps, Dialog as UnifiedDialog } from '@oneaudi/unified-web-components';
import { useAtom } from 'jotai';
import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { StyledDialogContent } from '~/components/dialog/styles/styled-dialog';
import { FootnoteRenderer } from '~/components/footnotes';
import { FootnoteProvider } from '~/components/footnotes/footnote-context';
import { state } from '~/storage';

type FocusDialogProps = DialogProps & {
	variant: 'focus-large' | 'focus-small';
	onCloseButtonAriaLabel: string;
};

type AlertDialogProps = DialogProps & {
	variant: 'alert';
};

export const Dialog = ({
	children,
	classNameDialog,
	onCloseButtonClick,
	classNameContent,
	open,
	variant = 'focus-large',
	triggerElement,
}: {
	children: React.ReactNode;
	classNameDialog?: string;
	classNameContent?: string;
	onCloseButtonClick?: () => void;
	open: boolean;
	variant?: 'focus-large' | 'focus-small' | 'alert';
	triggerElement?: React.ReactNode;
}) => {
	const { i18n } = useI18n();
	const commonProps = {
		open,
		children,
		className: classNameDialog,
		onClose: onCloseButtonClick,
		triggerButton: triggerElement,
	};

	const dialogProps =
		variant === 'alert'
			? ({ ...commonProps, variant: 'alert' } as AlertDialogProps)
			: ({
					...commonProps,
					variant,
					onCloseButtonAriaLabel: i18n('ngc.a11y.dialog.close'),
					hasCloseButton: true,
				} as FocusDialogProps);

	// Workaround to save the last focused element, so we can focus it again, when the dialog is closed to not jump on the page to another element.
	const [_open, _setOpen] = useState<undefined | boolean>(undefined);
	const lastFocusedElement = useRef<Element | null>(null);

	const [lastTrigger, setLastTrigger] = useAtom(state.lastTrigger);

	useEffect(() => {
		if (open) {
			lastFocusedElement.current = document.activeElement;
			_setOpen(true);
		}
		// Normally the dialog is not really closed when closing it in the UI, it is only hidden (e.g. trimlines)
		else {
			if (lastFocusedElement.current) {
				(lastFocusedElement.current as HTMLElement).focus();
			}

			if (lastTrigger) {
				let match: HTMLElement | null = null;
				const possibleElements = document.getElementsByClassName('top-item');

				for (let i = 0; i < possibleElements.length; i++) {
					for (let j = 0; j < possibleElements[i].children.length; j++) {
						for (let k = 0; k < possibleElements[i].children[j].children.length; k++) {
							if (
								(possibleElements[i].children[j].children[k] as HTMLElement).innerText === lastTrigger
							) {
								match = possibleElements[i].children[j] as HTMLElement;
								break;
							}
							if (match) break;
						}
						if (match) break;
					}
				}

				setLastTrigger(null);
				if (match) match.focus();
			}

			setLastTrigger(null);
			_setOpen(false);
		}

		// There are some use cases where the dialog is really removed from the dom when closed. So we also have to handle that case (e.g. more information in packages). Maybe this can be changed in the future to handle all the dialogs in the same way.
		return () => {
			(lastFocusedElement.current as HTMLElement)?.focus();
		};
	}, [open]);

	return (
		<UnifiedDialog {...dialogProps} open={!!_open}>
			<FootnoteProvider>
				<StyledDialogContent className={classNameContent}>
					{children}
					<FootnoteRenderer />
				</StyledDialogContent>
			</FootnoteProvider>
		</UnifiedDialog>
	);
};
